/**
 * ProgressRing — SVG donut that animates from 0% up to the given fraction.
 *
 * Used by the claims list to render turnover completion at a glance.
 * Centre slot renders the numeric percent label by default; callers may
 * override with children (e.g. show "DONE" on completion).
 */
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

interface ProgressRingProps {
  /** Progress fraction in [0, 1]. Values are clamped. */
  progress: number;
  size?: number;
  strokeWidth?: number;
  /** Stroke colour for the foreground arc. Defaults to the primary token. */
  color?: string;
  /** Stroke colour for the dim background ring. */
  trackColor?: string;
  /** Override the centre label; falls back to `${pct}%`. */
  children?: ReactNode;
  className?: string;
}

export function ProgressRing({
  progress,
  size = 48,
  strokeWidth = 4,
  color = 'var(--success)',
  trackColor = 'var(--border-subtle)',
  children,
  className,
}: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamped);
  const center = size / 2;
  const pct = Math.round(clamped * 100);

  return (
    <div
      className={className}
      style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          // Start at 12 o'clock and sweep clockwise.
          transform={`rotate(-90 ${center} ${center})`}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.max(10, size * 0.26),
          fontWeight: 700,
          color: 'var(--text-primary)',
          lineHeight: 1,
        }}
      >
        {children ?? `${pct}%`}
      </div>
    </div>
  );
}
